import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import { DEFAULT_SLUG } from './data/menuData.js';
import RoleRoute from './components/RoleRoute.jsx';
import './App.scss';

// Shared page shell (translated from the RawHTMLDesign layout shells). Named
// design layouts (LoginPortalSelectorLayout / PortfolioDirectorDashboardLayout /
// OwnerGovernanceReportsLayout) all reuse this base; ui-components-from-reference
// mounts pages under the matching named layout as they are generated.
const BaseLayout = lazy(() => import('./layouts/BaseLayout.jsx'));

// Public entry shell — same chrome as BaseLayout when a session is active, but
// drops the top navbar for anonymous visitors (the login card is the page).
const LoginShell = lazy(() => import('./layouts/LoginShell.jsx'));

// Pages.
const LoginPortalSelector = lazy(() => import('./pages/login-portal-selector/index.jsx'));
const PortfolioDirectorDashboard = lazy(
  () => import('./pages/portfolio-director-dashboard/index.jsx')
);
const ReraComplianceEngine = lazy(() => import('./pages/rera-compliance-engine/index.jsx'));
const RenewalRadar = lazy(() => import('./pages/renewal-radar/index.jsx'));
const OwnerGovernanceReports = lazy(() => import('./pages/owner-governance-reports/index.jsx'));
const YieldOptimiser = lazy(() => import('./pages/yield-optimiser/index.jsx'));
const LeasingAgentRenewalDesk = lazy(() => import('./pages/leasing-agent-renewal-desk/index.jsx'));
const IntegrationManagementHub = lazy(() => import('./pages/integration-management-hub/index.jsx'));
const NoticeCommunicationStudio = lazy(() => import('./pages/notice-communication-studio/index.jsx'));
const TenantRenewalPortal = lazy(() => import('./pages/tenant-renewal-portal/index.jsx'));
const BaselineOutcomeLedger = lazy(() => import('./pages/baseline-outcome-ledger/index.jsx'));

// Generic themed status pages.
const NotFound = lazy(() => import('./pages/status/NotFound.jsx'));
const Unauthorized = lazy(() => import('./pages/status/Unauthorized.jsx'));
const Forbidden = lazy(() => import('./pages/status/Forbidden.jsx'));
const ServerError = lazy(() => import('./pages/status/ServerError.jsx'));

// Page routes are added by ui-components-from-reference / implement-use-case as
// each page is generated, e.g.:
//   const DashboardPage = lazy(() => import('./pages/portfolio-director-dashboard/index.jsx'));
//   <Route element={<ProtectedRoute />}>
//     <Route path="portfolio-director-dashboard" element={<DashboardPage />} />
//   </Route>

const Loader = () => (
  <div className="d-flex justify-content-center py-5" style={{ paddingTop: 100 }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* PUBLIC entry — Login & Portal Selector, under the navbar-less
                (when anonymous) LoginShell. Registered ahead of the RoleRoute
                guards so anonymous users can reach it. Both the landing slug
                and the `/login` redirect target (used by the guards and
                logout) resolve here instead of the 404 catch-all. */}
            <Route element={<LoginShell />}>
              <Route path="login-portal-selector" element={<LoginPortalSelector />} />
              <Route path="login" element={<LoginPortalSelector />} />
            </Route>

            <Route element={<BaseLayout />}>
              {/* Landing → default page (routes by slug). */}
              <Route index element={<Navigate to={`/${DEFAULT_SLUG}`} replace />} />

              {/* Page routes go here as pages are generated. */}
              {/* Portfolio Director Dashboard — governed AKM view. Gated to the
                  dashboard business roles; Owner-Principals are redirected to
                  the Owner Portal by RoleRoute. */}
              <Route
                element={
                  <RoleRoute
                    roles={['portfolio_director', 'asset_manager', 'cfo', 'compliance_officer']}
                  />
                }
              >
                <Route
                  path="portfolio-director-dashboard"
                  element={<PortfolioDirectorDashboard />}
                />
              </Route>

              {/* RERA Compliance Engine — gated to Compliance Officer + Portfolio
                  Director (the Compliance & Audit Hub roles). */}
              <Route
                element={<RoleRoute roles={['compliance_officer', 'portfolio_director']} />}
              >
                <Route path="rera-compliance-engine" element={<ReraComplianceEngine />} />
              </Route>

              {/* Renewal Radar — gated to Renewal Operations roles (Leasing
                  Agent + Asset Manager + Portfolio Director). */}
              <Route
                element={
                  <RoleRoute roles={['leasing_agent', 'asset_manager', 'portfolio_director']} />
                }
              >
                <Route path="renewal-radar" element={<RenewalRadar />} />
                <Route path="yield-optimiser" element={<YieldOptimiser />} />
                <Route path="leasing-agent-renewal-desk" element={<LeasingAgentRenewalDesk />} />
              </Route>

              {/* Owner Governance & Reports — the Owner Portal. Asset Manager /
                  Portfolio Director get the operator view (mandate config +
                  report packs); Owner-Principals get their partitioned portal
                  view (permitted here so RoleRoute does not redirect them). */}
              <Route
                element={
                  <RoleRoute
                    roles={['asset_manager', 'portfolio_director', 'owner_principal']}
                  />
                }
              >
                <Route path="owner-governance-reports" element={<OwnerGovernanceReports />} />
              </Route>

              {/* Integration Management Hub — signature provider config. Editable
                  by Portfolio Director / CFO / Xamun Ops; Asset Manager is a
                  read-only viewer. */}
              <Route
                element={
                  <RoleRoute
                    roles={['portfolio_director', 'cfo', 'xamun_ops', 'asset_manager']}
                  />
                }
              >
                <Route path="integration-management-hub" element={<IntegrationManagementHub />} />
              </Route>

              {/* Notice & Communication Studio — slot-locked bilingual notices,
                  gated to the Renewal Operations roles (Leasing Agent + Asset
                  Manager + Portfolio Director). */}
              <Route
                element={
                  <RoleRoute roles={['leasing_agent', 'asset_manager', 'portfolio_director']} />
                }
              >
                <Route path="notice-communication-studio" element={<NoticeCommunicationStudio />} />
              </Route>

              {/* Tenant Renewal Portal — tenant-facing PWA (review, decide,
                  execute). Gated to the tenant business role. */}
              <Route element={<RoleRoute roles={['tenant']} />}>
                <Route path="tenant-renewal-portal" element={<TenantRenewalPortal />} />
              </Route>

              {/* Baseline & Outcome Ledger — CFO audit + billing reconciliation.
                  Portfolio Director gets read-only review; CFO reconciles. */}
              <Route element={<RoleRoute roles={['cfo', 'portfolio_director']} />}>
                <Route path="baseline-outcome-ledger" element={<BaselineOutcomeLedger />} />
              </Route>

              {/* Generic status pages. */}
              <Route path="401" element={<Unauthorized />} />
              <Route path="403" element={<Forbidden />} />
              <Route path="500" element={<ServerError />} />
              <Route path="404" element={<NotFound />} />

              {/* Catch-all → 404 (any unresolved slug lands here). */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
